import React, { useEffect, useState } from 'react';
import { PageWithNavigation } from '@/src/components';
import { PrimaryCTAButton, Card, Ul, Li } from '@ovotech/nebula';
import {
  P,
  Margin,
  Divider,
  Modal,
  SecondaryCTAButton,
  Heading1,
} from '@ovotech/element-v4';
import { Strong, TextLink } from '@ovotech/element-v5';
import { ROUTE_ANYTIME_ELIGIBILITY } from '@/src/constants/routes';
import { useHistory } from 'react-router-dom-v5';
import styled from 'styled-components';
import Calendar from '@/src/resources/chargeAnytime/calendar-v2.svg';
import Present from '@/src/resources/chargeAnytime/present.svg';
import CarCharger from '@/src/resources/chargeAnytime/car-charger.png';
import Coin from '@/src/resources/chargeAnytime/coin.png';
import ChevronRight from '@/src/resources/chargeAnytime/chevron-right.svg';
import { mqSmallDown } from '@/src/utils/mediaQuery';
import { useAnalytics } from '@ovotech/ui-tools';
import { EV } from '@monovo/ev-anytime';

const Features = styled(Margin)`
  display: flex;
  flex-direction: column;
`;

const FeatureRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
`;

const FeatureIcon = styled.img`
  width: 48px;
  height: 48px;
`;

const FeatureTitle = styled(P)`
  font-size: 32px;
  line-height: 32px;
  font-weight: 900;
  color: #044d29;
`;

const FeatureSubTitle = styled(P)`
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: #0a9828;
`;

const ModalSectionText = styled(P)`
  font-size: 20px;
  ${mqSmallDown(`
    font-size: 18px;
  `)};
`;

const ModalButtonContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  align-items: stretch;

  ${mqSmallDown(`
    grid-template-columns: 1fr;
  `)};
`;

const FeatureCard = styled.div`
  border-radius: 22px;
  border: 1px solid #635e58;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const FeatureCardHeader = styled.div`
  padding: 28px 24px;
  background-color: #f9f6f3;
  border-radius: 22px 22px 0 0;
  flex: 1;
`;

const FeatureCardBody = styled.div`
  padding: 24px;
  background-color: white;
  border-radius: 0 0 22px 22px;
  flex: 2;
`;

const DescriptionCard = styled(Card)`
  margin: 32px 0;
  padding: 16px;
  ${mqSmallDown(`
    padding: 0;
    background-color: transparent;
    margin: 70px 0 16px 0;
  `)};
`;

const ButtonsContainer = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: flex-end;
  gap: 24px;
  ${mqSmallDown(`
    flex-direction: column-reverse;
    margin-bottom: 50px;
  `)};
`;

const SplitWords = styled.div`
  display: none;
  ${mqSmallDown(`
    display: block;
    margin-top: 16px;
  `)};
`;

const Title = styled(Heading1)`
  color: white;
  font-size: 48px;
  margin-top: 2.4rem;
`;

const StyledPrimaryCTAButton = styled(PrimaryCTAButton)`
  background-color: #00261c;
`;

const StyledTextLink = styled(TextLink)`
  font-weight: var(--font-weight-book);
  cursor: pointer;
`;

const IntroText = styled(P)`
  line-height: 2rem;

  & + & {
    margin-top: var(--space-300);
  }
`;

const PrimaryButtonIcon = styled.img`
  display: inline;
  width: 16px;
  height: 16px;
  color: white;
  vertical-align: sub;
  margin-left: 5px;
  ${mqSmallDown(`
    display: none;
  `)};
`;

export const Introduction = () => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);

  const { dispatch: analyticsDispatch } = useAnalytics();

  const analyticsEventProperties = {
    Screen: EV.SCREENS.INTRO,
    journeyName: 'Charge Anytime',
  };

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY.CHARGE_ANYTIME_INTRO_PAGE_VIEWED,
      properties: analyticsEventProperties,
    });
    // `analyticsDispatch` is not memoized and would cause useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageWithNavigation title="EV Charge Anytime - Eligibility">
      <Title>
        Charge <SplitWords /> Anytime
      </Title>
      <div>
        <DescriptionCard>
          <Margin bottom={4}>
            <IntroText>Say hello to our smart charging.</IntroText>
            <IntroText>
              Answer 4 quick questions to check if your car and charger are
              compatible.
            </IntroText>
            <IntroText>
              You can choose <Strong>Pay monthly</Strong> or{' '}
              <Strong>Pay as you go</Strong>.
            </IntroText>
          </Margin>
          <StyledTextLink
            onClick={() => {
              analyticsDispatch({
                type: 'click',
                name: EV.ELIGIBILITY
                  .CHARGE_ANYTIME_INTRO_PAGE_SCREEN_DETAILS_CLICKED,
                properties: analyticsEventProperties,
              });
              setIsOpen(true);
            }}
          >
            Learn how smart charging works
          </StyledTextLink>
        </DescriptionCard>

        <CardsGrid>
          <FeatureCard>
            <FeatureCardHeader>
              <FeatureTitle>Pay monthly</FeatureTitle>
              <FeatureSubTitle>
                Flexible home and public charging - no contract
              </FeatureSubTitle>
            </FeatureCardHeader>
            <FeatureCardBody>
              <Features>
                <FeatureRow>
                  <FeatureIcon src={Calendar} alt={'calendar icon'} />
                  <Margin left={4}>
                    <P>
                      <Strong>Predictability</Strong>
                    </P>
                  </Margin>
                </FeatureRow>
                <FeatureRow>
                  <FeatureIcon src={Present} alt={'switch icon'} />
                  <Margin left={4}>
                    <P>
                      <Strong>Public charging voucher and driver perks</Strong>
                    </P>
                  </Margin>
                </FeatureRow>
                <FeatureRow>
                  <FeatureIcon src={Coin} alt={'coin icon'} />
                  <Margin left={4}>
                    <P>
                      <Strong>Best savings for home and on the road</Strong>
                    </P>
                  </Margin>
                </FeatureRow>
              </Features>
            </FeatureCardBody>
          </FeatureCard>

          <FeatureCard>
            <FeatureCardHeader>
              <FeatureTitle>Pay as you go</FeatureTitle>
              <FeatureSubTitle>Home charging on demand</FeatureSubTitle>
            </FeatureCardHeader>
            <FeatureCardBody>
              <Features>
                <FeatureRow>
                  <FeatureIcon src={Coin} alt={'coin icon'} />
                  <Margin left={4}>
                    <P>
                      <Strong>Save on your home charging</Strong>
                    </P>
                  </Margin>
                </FeatureRow>
                <FeatureRow>
                  <FeatureIcon src={CarCharger} alt={'coin icon'} />
                  <Margin left={4}>
                    <P>
                      <Strong>Ideal for infrequent charging</Strong>
                    </P>
                  </Margin>
                </FeatureRow>
              </Features>
            </FeatureCardBody>
          </FeatureCard>
        </CardsGrid>

        <ButtonsContainer>
          <StyledPrimaryCTAButton
            data-testid="next-button"
            onClick={() => {
              analyticsDispatch({
                type: 'click',
                name: EV.ELIGIBILITY.CHARGE_ANYTIME_INTRO_PAGE_NEXT_CLICKED,
                properties: analyticsEventProperties,
              });
              history.push(ROUTE_ANYTIME_ELIGIBILITY.SMART_CHARGER);
            }}
          >
            Check compatibility
            <PrimaryButtonIcon src={ChevronRight} />
          </StyledPrimaryCTAButton>
        </ButtonsContainer>
      </div>
      <Modal
        title="How does it work?"
        isOpen={isOpen}
        onClose={() => {
          analyticsDispatch({
            type: 'click',
            name: EV.ELIGIBILITY
              .CHARGE_ANYTIME_ELIGIBILITY_INTRO_SCREEN_DETAILS_CLOSE_CTA_CLICKED,
            properties: analyticsEventProperties,
          });
          setIsOpen(false);
        }}
      >
        <ModalSectionText>
          <Strong>You'll need:</Strong>
        </ModalSectionText>
        <Margin top={2} bottom={4}>
          <Ul>
            <Li>
              <ModalSectionText>A compatible EV or charger</ModalSectionText>
            </Li>
            <Li>
              <ModalSectionText>A smart meter</ModalSectionText>
            </Li>
          </Ul>
        </Margin>
        <ModalSectionText>
          <Strong>Smart charging</Strong>
        </ModalSectionText>
        <Margin top={2} bottom={4}>
          <ModalSectionText>
            Smart charging lets us give you great EV charging rates. Set the
            time you need your car ready and we’ll charge it at the greenest
            possible time.
          </ModalSectionText>
        </Margin>
        <ModalSectionText>
          <Strong>Charge anytime</Strong>
        </ModalSectionText>
        <Margin top={2} bottom={4}>
          <ModalSectionText>
            Smart charge day or night – no need to worry about peak and off-peak
            pricing.
          </ModalSectionText>
        </Margin>
        <ModalSectionText>
          <Strong>Urgent charge</Strong>
        </ModalSectionText>
        <Margin top={2} bottom={4}>
          <ModalSectionText>
            Need to boost your charge ahead of the smart schedule? Urgent charge
            tops you up instantly, at your home energy rate.
          </ModalSectionText>
        </Margin>
        <Divider type="differentiated" />
        <Margin top={4}>
          <ModalButtonContainer>
            <SecondaryCTAButton
              onClick={() => {
                analyticsDispatch({
                  type: 'click',
                  name: EV.ELIGIBILITY
                    .CHARGE_ANYTIME_ELIGIBILITY_INTRO_SCREEN_DETAILS_CLOSE_CTA_CLICKED,
                  properties: analyticsEventProperties,
                });
                setIsOpen(false);
              }}
            >
              Close
            </SecondaryCTAButton>
          </ModalButtonContainer>
        </Margin>
      </Modal>
    </PageWithNavigation>
  );
};
